import { useEffect, useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
    Rocket, FolderKanban, ShieldCheck, Loader2, ArrowRight, PlayCircle,
    Wallet, CheckCircle2, Clock3,
} from "lucide-react";
import { api, useAuth } from "@/lib/auth";
import { AccountLayout } from "@/components/account/AccountLayout";
import { usePageMeta } from "@/hooks/usePageMeta";

// DD/MM/YYYY across the customer portal (AU convention).
const ddmmyyyy = (iso) => {
    if (!iso) return "—";
    try {
        const d = new Date(iso.length === 10 ? `${iso}T00:00:00` : iso);
        if (Number.isNaN(d.getTime())) return "—";
        return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
    } catch (e) { return "—"; }
};

const fadeUp = {
    hidden: { opacity: 0, y: 12 },
    show: (i = 0) => ({
        opacity: 1, y: 0,
        transition: { duration: 0.5, delay: i * 0.06, ease: [0.16, 1, 0.3, 1] },
    }),
};

const STATUS_LABEL = {
    new: "Received", approved: "Approved", scheduled: "Scheduled",
    live: "Live", done: "Delivered", completed: "Delivered",
    rejected: "Cancelled", cancelled: "Cancelled",
};

const STATUS_TONE = {
    new: "text-slate-400", approved: "text-emerald-400", scheduled: "text-emerald-400",
    live: "text-[#FF5A00]", done: "text-slate-500", completed: "text-slate-500",
    rejected: "text-red-400", cancelled: "text-red-400",
};

/**
 * Compact "change password" card. Only shown when the user already has a
 * password — the earlier "No password yet — add one to sign in on new
 * devices." nudge was removed as part of the portal simplification.
 */
const SecurityCard = () => {
    const { user, setPassword, refresh } = useAuth();
    const [pw, setPw] = useState("");
    const [currentPw, setCurrentPw] = useState("");
    const [busy, setBusy] = useState(false);
    const [open, setOpen] = useState(false);

    if (!user?.has_password) return null;

    const save = async (e) => {
        e?.preventDefault?.();
        if (pw.length < 8) { toast.error("Password must be at least 8 characters."); return; }
        if (!currentPw) { toast.error("Enter your current password."); return; }
        setBusy(true);
        try {
            await setPassword(pw, currentPw);
            await refresh();
            setPw(""); setCurrentPw(""); setOpen(false);
            toast.success("Password saved.");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not save the password.");
        } finally { setBusy(false); }
    };

    return (
        <div className="rounded-2xl border border-white/10 bg-[#1B2E55]/60 p-5" data-testid="account-security-card">
            <div className="flex items-center gap-2">
                <ShieldCheck size={15} className="text-[#FF5A00]" />
                <div className="font-mono-tech text-[10px] uppercase tracking-[0.3em] text-[#FF5A00]">/ Security</div>
            </div>
            <div className="mt-2 text-sm text-white">Password is set for this account.</div>
            {!open ? (
                <button onClick={() => setOpen(true)} data-testid="account-set-password-btn"
                    className="mt-3 inline-flex items-center gap-2 text-[10px] font-mono-tech uppercase tracking-widest text-slate-400 hover:text-[#FF5A00] transition-colors">
                    Change password <ArrowRight size={11} />
                </button>
            ) : (
                <form onSubmit={save} className="mt-3 space-y-2 max-w-sm">
                    <input type="password" value={currentPw} onChange={(e) => setCurrentPw(e.target.value)} placeholder="Current password"
                        data-testid="account-current-password-input"
                        className="w-full bg-[#0F1E3B] border border-white/10 rounded-xl text-white px-3 py-2.5 text-xs focus:border-[#FF5A00] outline-none" />
                    <div className="flex items-stretch gap-2">
                        <input type="password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="New password (min 8)"
                            data-testid="account-password-input"
                            className="flex-1 bg-[#0F1E3B] border border-white/10 rounded-xl text-white px-3 py-2.5 text-xs min-w-0 focus:border-[#FF5A00] outline-none" />
                        <button type="submit" disabled={busy} data-testid="account-password-save-btn"
                            className="inline-flex items-center gap-2 bg-[#FF5A00] hover:bg-[#E65200] disabled:opacity-60 text-white px-4 rounded-xl text-xs font-semibold uppercase tracking-widest shrink-0">
                            {busy ? <Loader2 size={12} className="animate-spin" /> : "Save"}
                        </button>
                    </div>
                </form>
            )}
        </div>
    );
};

const Campaigns = ({ campaigns }) => {
    if (campaigns === null) {
        return (
            <div className="space-y-2" data-testid="account-campaigns-loading">
                <div className="animate-pulse bg-white/10 rounded-2xl h-16" />
                <div className="animate-pulse bg-white/10 rounded-2xl h-16" />
            </div>
        );
    }
    if (!campaigns.length) {
        return (
            <div className="rounded-2xl border border-dashed border-white/15 p-8 text-center" data-testid="account-campaigns-empty">
                <PlayCircle size={20} className="text-[#FF5A00] mx-auto" />
                <div className="mt-3 font-display font-black tracking-tight text-lg text-white">No campaigns yet</div>
                <p className="mt-1 text-xs text-slate-400 max-w-sm mx-auto">
                    Your first billboard is a few clicks away. It takes about four minutes.
                </p>
                <Link to="/book" data-testid="account-empty-cta"
                    className="mt-5 inline-flex items-center gap-2 bg-[#FF5A00] hover:bg-[#E65200] text-white px-5 py-2.5 rounded-full text-xs font-semibold uppercase tracking-widest transition-colors">
                    <Rocket size={13} /> Book your first
                </Link>
            </div>
        );
    }
    return (
        <div className="space-y-2" data-testid="account-campaigns">
            {campaigns.map((c) => {
                const status = c.pipeline_status || "new";
                return (
                    <Link
                        key={c.id}
                        to={`/portal?campaign=${c.id}`}
                        data-testid={`account-campaign-${c.id}`}
                        className="group flex items-center gap-4 rounded-2xl border border-white/10 bg-[#1B2E55]/60 px-4 py-3.5 hover:border-[#FF5A00]/50 hover:bg-[#1B2E55] transition-all"
                    >
                        <span className="w-9 h-9 rounded-xl border border-[#FF5A00]/40 bg-[#FF5A00]/10 flex items-center justify-center shrink-0">
                            {status === "live" ? <PlayCircle size={15} className="text-[#FF5A00]" />
                                : status === "done" || status === "completed" ? <CheckCircle2 size={15} className="text-[#FF5A00]" />
                                : <Clock3 size={15} className="text-[#FF5A00]" />}
                        </span>
                        <span className="min-w-0 flex-1">
                            <span className="block text-sm font-semibold text-white truncate">
                                {c.business_name || "Campaign"} · {(c.cities || []).join(", ") || "TBC"}
                            </span>
                            <span className="block text-xs text-slate-400 truncate">
                                {c.launch_date ? `Launch ${ddmmyyyy(c.launch_date)}` : "Launch TBC"}
                                {c.bookings_count > 0 && ` · ${c.bookings_count} booking${c.bookings_count === 1 ? "" : "s"}`}
                            </span>
                        </span>
                        <span className={`text-[10px] font-mono-tech uppercase tracking-widest shrink-0 ${STATUS_TONE[status] || "text-slate-400"}`}>
                            {STATUS_LABEL[status] || status}
                        </span>
                        <ArrowRight size={13} className="text-slate-500 group-hover:text-[#FF5A00] transition-colors shrink-0" />
                    </Link>
                );
            })}
        </div>
    );
};

export default function AccountHome() {
    usePageMeta({ title: "Dashboard · NOXN", description: "Your campaigns, one place." });
    const { user } = useAuth();
    const [campaigns, setCampaigns] = useState(null);
    const [balance, setBalance] = useState(null);

    useEffect(() => {
        api.get("/portal/campaigns")
            .then(({ data }) => setCampaigns(data || []))
            .catch(() => setCampaigns([]));
        api.get("/plays/balance").then(({ data }) => setBalance(data.balance || 0)).catch(() => setBalance(0));
    }, []);

    const greeting = useMemo(() => {
        const h = new Date().getHours();
        if (h < 12) return "Good morning";
        if (h < 17) return "Good afternoon";
        return "Good evening";
    }, []);
    const firstName = (user?.name || user?.email || "").split(/[\s@]/)[0];

    // Merged nav concept: staff/admin/super_admin have their own "Dashboard"
    // at /dashboard — /account is client-only.
    if (user && user.role !== "client") return <Navigate to="/dashboard" replace />;

    return (
        <AccountLayout title="Dashboard">
            <div data-testid="account-home">
                <motion.div variants={fadeUp} initial="hidden" animate="show" custom={0}>
                    <div className="font-mono-tech text-[10px] uppercase tracking-[0.35em] text-[#FF5A00]">/ {greeting}</div>
                    <h1 className="mt-2 font-display font-black tracking-tighter text-4xl sm:text-5xl text-white" data-testid="account-greeting">
                        {firstName || "There"}, let&apos;s book a screen.
                    </h1>
                    {balance !== null && balance > 0 && (
                        <div className="mt-3 inline-flex items-center gap-2 text-xs text-slate-400" data-testid="account-balance-inline">
                            <Wallet size={12} className="text-[#FF5A00]" />
                            Play balance: <span className="text-white font-mono-tech">{balance.toLocaleString()}</span> plays — auto-applies at checkout.
                        </div>
                    )}
                </motion.div>

                <motion.div variants={fadeUp} initial="hidden" animate="show" custom={1} className="mt-8">
                    <Link
                        to="/book"
                        data-testid="account-primary-cta"
                        className="group inline-flex items-center gap-3 bg-[#FF5A00] hover:bg-[#E65200] text-white px-6 py-4 rounded-full text-sm font-semibold uppercase tracking-widest transition-colors"
                    >
                        <Rocket size={15} /> Book a screen
                        <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" />
                    </Link>
                </motion.div>

                <motion.div variants={fadeUp} initial="hidden" animate="show" custom={2} className="mt-12">
                    <div className="flex items-baseline justify-between mb-4">
                        <div className="font-mono-tech text-[10px] uppercase tracking-[0.3em] text-slate-500">/ Your campaigns</div>
                        {campaigns && campaigns.length > 0 && (
                            <Link to="/portal" className="text-[10px] font-mono-tech uppercase tracking-widest text-slate-400 hover:text-[#FF5A00] transition-colors" data-testid="account-see-all">
                                See all →
                            </Link>
                        )}
                    </div>
                    <Campaigns campaigns={campaigns} />
                </motion.div>

                <motion.div variants={fadeUp} initial="hidden" animate="show" custom={3} className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-2xl">
                    <Link to="/book" data-testid="account-quick-book"
                        className="group rounded-2xl border border-white/10 bg-[#1B2E55]/50 px-4 py-3.5 hover:border-[#FF5A00]/50 hover:bg-[#1B2E55] transition-all inline-flex items-center gap-3">
                        <Rocket size={16} className="text-[#FF5A00]" />
                        <span className="flex-1">
                            <span className="block text-sm text-white font-semibold">Book another screen</span>
                            <span className="block text-xs text-slate-400">Pick, pay, and light it up in one page.</span>
                        </span>
                        <ArrowRight size={13} className="text-slate-500 group-hover:text-[#FF5A00] transition-colors" />
                    </Link>
                    <Link to="/portal" data-testid="account-quick-campaigns"
                        className="group rounded-2xl border border-white/10 bg-[#1B2E55]/50 px-4 py-3.5 hover:border-[#FF5A00]/50 hover:bg-[#1B2E55] transition-all inline-flex items-center gap-3">
                        <FolderKanban size={16} className="text-[#FF5A00]" />
                        <span className="flex-1">
                            <span className="block text-sm text-white font-semibold">All campaigns</span>
                            <span className="block text-xs text-slate-400">Contracts, invoices and delivery status.</span>
                        </span>
                        <ArrowRight size={13} className="text-slate-500 group-hover:text-[#FF5A00] transition-colors" />
                    </Link>
                </motion.div>

                {user?.has_password && (
                    <motion.div variants={fadeUp} initial="hidden" animate="show" custom={4} className="mt-10 max-w-xl">
                        <SecurityCard />
                    </motion.div>
                )}
            </div>
        </AccountLayout>
    );
}
